"use client";

/**
 * ChatCommandMenu Component
 *
 * A sectioned "/" command menu that opens when the user types "/" at the
 * start of the chat input or activates a "/" affordance button.
 *
 * Reuses the fixed-position popup pattern established by MentionAutocomplete:
 * positioned near the caret, keyboard-navigable with ArrowUp/Down + Enter + Esc.
 */

import type { CommandMenuView } from "./ChatCommandMenu.sections";

import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  buildMainSections,
  buildSkillSubItems,
  buildWorkflowSubItems,
} from "./ChatCommandMenu.sections";
import { useCommandMenuKeyboard } from "./ChatCommandMenu.keyboard";
import { MenuBody, MenuFooter, MenuHeader } from "./ChatCommandMenu.parts";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * Minimal skill shape needed to render a selectable `/` command-menu row.
 *
 * Sourced from the real skills catalogue (`GET /admin/v1/skills/catalog`) so
 * the Skills section reflects what the assistant can actually use.
 */
export interface SkillCommandEntry {
  /** Stable catalogue id, used to build the `@s_<id>` reference token. */
  id: string;
  /** Display name shown in the row. */
  name: string;
  /** Short description shown beneath the name. */
  description: string;
}

export interface ChatCommandMenuItem {
  id: string;
  /** Translated label — caller must resolve via t() before passing. */
  label: string;
  /** Iconify icon identifier. */
  icon: string;
  /** When true the row renders a toggle switch instead of a plain action. */
  isToggle?: boolean;
  /** Current toggle state (only relevant when isToggle is true). */
  toggleValue?: boolean;
  /** Called with the new boolean value when the toggle changes. */
  onToggle?: (val: boolean) => void;
  /** Called when an action item is activated (click or Enter). */
  onSelect?: () => void;
  /** Prevents activation of the item. */
  isDisabled?: boolean;
  /** Human-readable explanation shown via title attribute when disabled. */
  disabledReason?: string;
  /**
   * When set, the row renders as a skill shortcut: `/{key}` in medium weight
   * above a muted helper line (this description text).
   */
  skillKey?: string;
  /** Muted helper text shown below the skill key in the row. */
  skillDescription?: string;
  /**
   * Muted helper line shown below the label for non-skill rows (e.g. workflow
   * rows show the workflow name on top and its description here).
   */
  description?: string;
  /** When true, the row shows a tick to indicate it is the active selection. */
  isCurrent?: boolean;
  /**
   * When true, activating the row does NOT close the menu (used for drill-in
   * entries that open a sub-view, e.g. "Workflows").
   */
  keepOpenOnSelect?: boolean;
  /** When true, a hairline divider is rendered above the row. */
  dividerBefore?: boolean;
}

export interface ChatCommandMenuSection {
  id: string;
  /** Translated section label — caller resolves via t(). */
  label: string;
  items: ChatCommandMenuItem[];
}

export interface ChatCommandMenuProps {
  /** Whether the menu is currently visible. */
  visible: boolean;
  /** Fixed-position coordinates for the popup. */
  position: { top: number; left: number };

  // --- Tool states ---
  isWebSearchEnabled: boolean;
  isKnowledgeHubEnabled: boolean;
  /** Whether the bottom-right model selector is currently shown. */
  modelSelectorEnabled: boolean;

  // --- Visibility flags (permission gates) ---
  showWebSearch: boolean;
  showKnowledgeHub: boolean;
  /** Whether to show the Model Selector toggle row in the Tools section. */
  showModelSelector: boolean;
  showPrompts: boolean;
  showPrompto: boolean;
  showConfiguration: boolean;

  // --- Disabled states ---
  isConfigurationDisabled: boolean;
  isPromptLocked: boolean;

  // --- Workflows (in-menu workflow picker) ---
  /** Workflows the user can switch the chat to. When empty the section is hidden. */
  workflows: { id: string; name: string; description?: string; icon?: string }[];
  /** Whether to render the Workflows section at all (permission / availability gate). */
  showWorkflows: boolean;
  /** Id of the workflow currently active in the chat, marked with a tick. */
  activeWorkflowId?: string | null;
  /** Brand-resolved heading for the Workflows section (falls back to a localised default). */
  workflowsLabel?: string;

  // --- Skills (real skills catalogue) ---
  /** Skills the assistant can use, surfaced as `/name` rows. When empty the section is hidden. */
  skills: SkillCommandEntry[];
  /**
   * Text typed after the leading `/` — used to filter skill rows by substring
   * match on id and name. Empty string shows all skills.
   */
  skillQuery: string;

  // --- Handlers ---
  onWebSearchToggle: (val: boolean) => void;
  onKnowledgeHubToggle: () => void;
  /** Called with the new boolean when the user toggles the model selector row. */
  onModelSelectorToggle: (val: boolean) => void;
  onBrowsePrompts: () => void;
  onPrompto: () => void;
  onOpenConfiguration: () => void;
  /** Called with the skill's catalogue id when the user activates a skill row. */
  onSelectSkill: (skillId: string) => void;
  /** Called with a workflow id when the user activates a workflow row. */
  onSelectWorkflow: (workflowId: string) => void;
  /** Navigate to the full Workflows index ("Browse all workflows" footer action). */
  onBrowseAllWorkflows: () => void;
  /** Create a new workflow. When omitted, the "New workflow" row is hidden (RBAC gate). */
  onCreateWorkflow?: () => void;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * ChatCommandMenu renders a fixed-position, sectioned command palette that
 * surfaces tools, prompts, model selection, and configuration in one place.
 *
 * Only ACTION items (non-toggle) participate in keyboard navigation so that
 * toggle rows remain mouse/pointer-first without breaking arrow-key flow.
 */
export const ChatCommandMenu: React.FC<ChatCommandMenuProps> = ({
  visible,
  position,
  isWebSearchEnabled,
  isKnowledgeHubEnabled,
  modelSelectorEnabled,
  showWebSearch,
  showKnowledgeHub,
  showModelSelector,
  showPrompts,
  showPrompto,
  showConfiguration,
  isConfigurationDisabled,
  isPromptLocked,
  workflows,
  showWorkflows,
  activeWorkflowId,
  workflowsLabel,
  skills,
  skillQuery,
  onWebSearchToggle,
  onKnowledgeHubToggle,
  onModelSelectorToggle,
  onBrowsePrompts,
  onPrompto,
  onOpenConfiguration,
  onSelectSkill,
  onSelectWorkflow,
  onBrowseAllWorkflows,
  onCreateWorkflow,
  onClose,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  // Index into the flat list of ACTION items (toggle rows excluded).
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Which view the menu is showing: the main sections, or a drill-in sub-view.
  const [view, setView] = useState<CommandMenuView>("main");

  // ---------------------------------------------------------------------------
  // Build section descriptors from props
  // ---------------------------------------------------------------------------

  const sections: ChatCommandMenuSection[] = React.useMemo(
    () =>
      buildMainSections({
        t,
        skills,
        showWebSearch,
        showKnowledgeHub,
        showModelSelector,
        showPrompts,
        showPrompto,
        showConfiguration,
        showWorkflows,
        workflows,
        workflowsLabel,
        isWebSearchEnabled,
        isKnowledgeHubEnabled,
        modelSelectorEnabled,
        isConfigurationDisabled,
        isPromptLocked,
        onWebSearchToggle,
        onKnowledgeHubToggle,
        onModelSelectorToggle,
        onBrowsePrompts,
        onPrompto,
        onOpenConfiguration,
        openView: setView,
      }),
    [
      t,
      skills,
      showWebSearch,
      showKnowledgeHub,
      showModelSelector,
      showPrompts,
      showPrompto,
      showConfiguration,
      showWorkflows,
      workflows,
      workflowsLabel,
      isWebSearchEnabled,
      isKnowledgeHubEnabled,
      modelSelectorEnabled,
      isConfigurationDisabled,
      isPromptLocked,
      onWebSearchToggle,
      onKnowledgeHubToggle,
      onModelSelectorToggle,
      onBrowsePrompts,
      onPrompto,
      onOpenConfiguration,
    ],
  );

  // Items shown inside the Workflows sub-view: the filterable workflow list
  // followed by the "Browse all workflows" / "New workflow" footer actions.
  const workflowSubItems: ChatCommandMenuItem[] = React.useMemo(
    () =>
      buildWorkflowSubItems({
        showWorkflows,
        skillQuery,
        workflows,
        activeWorkflowId,
        t,
        onSelectWorkflow,
        onBrowseAllWorkflows,
        onCreateWorkflow,
      }),
    [
      showWorkflows,
      skillQuery,
      workflows,
      activeWorkflowId,
      t,
      onSelectWorkflow,
      onBrowseAllWorkflows,
      onCreateWorkflow,
    ],
  );

  // Items shown inside the Skills sub-view: the filterable skill list. Selecting
  // a row inserts an `@s_<id>` reference token via onSelectSkill.
  const skillSubItems: ChatCommandMenuItem[] = React.useMemo(
    () => buildSkillSubItems({ skills, skillQuery, onSelectSkill }),
    [skills, skillQuery, onSelectSkill],
  );

  // Flat list of only the ACTION items (non-toggle, non-disabled) for keyboard
  // nav. View-aware: the Workflows sub-view navigates its own items.
  const actionItems = React.useMemo(() => {
    const source =
      view === "workflows"
        ? workflowSubItems
        : view === "skills"
          ? skillSubItems
          : sections.flatMap((s) => s.items);

    return source.filter((item) => !item.isToggle && !item.isDisabled);
  }, [view, workflowSubItems, skillSubItems, sections]);

  // Reset selection when the menu opens, the view changes, or the list changes.
  useEffect(() => {
    setSelectedIndex(0);
  }, [visible, view, actionItems.length]);

  // Reset to the main view whenever the menu is reopened.
  useEffect(() => {
    if (!visible) setView("main");
  }, [visible]);

  // Scroll the highlighted item into view whenever selection changes.
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current.querySelector<HTMLElement>(
      `[data-action-id="${actionItems[selectedIndex]?.id}"]`,
    );

    if (el) {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex, actionItems]);

  // ---------------------------------------------------------------------------
  // Keyboard navigation
  // ---------------------------------------------------------------------------

  useCommandMenuKeyboard({
    visible,
    view,
    actionItems,
    selectedIndex,
    setSelectedIndex,
    setView,
    onClose,
  });

  // ---------------------------------------------------------------------------
  // Early return
  // ---------------------------------------------------------------------------

  if (!visible) return null;

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  return (
    <div
      ref={containerRef}
      aria-label={t("chatCommandMenu.header")}
      className="fixed z-50 bg-content1 border border-divider rounded-xl shadow-lg min-w-70 max-w-xs overflow-hidden"
      role="menu"
      style={{
        top: position.top,
        left: position.left,
        transform: "translateY(-100%)",
      }}
      tabIndex={-1}
      onMouseDown={(e) => e.preventDefault()}
    >
      <MenuHeader
        view={view}
        workflowsLabel={workflowsLabel}
        onBack={() => setView("main")}
        onClose={onClose}
      />

      <MenuBody
        actionItems={actionItems}
        sections={sections}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
        skillSubItems={skillSubItems}
        view={view}
        workflowSubItems={workflowSubItems}
        onClose={onClose}
      />

      <MenuFooter />
    </div>
  );
};

export default ChatCommandMenu;
